// System Architecture page - Knowledge Graph of D365 Application - 145 lines ✅
import { useState } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Database,
  Component,
  GitBranch,
  Tag,
  Network,
  Key,
  Brain,
  Layers
} from 'lucide-react'
import { EntitySection } from '@/components/architecture/EntitySection'
import { ComponentSection } from '@/components/architecture/ComponentSection'
import { RelationshipSection } from '@/components/architecture/RelationshipSection'
import { AttributeSection } from '@/components/architecture/AttributeSection'
import { OntologySection } from '@/components/architecture/OntologySection'
import { IdentifierSection } from '@/components/architecture/IdentifierSection'
import { InferenceSection } from '@/components/architecture/InferenceSection'

export function SystemArchitecture(): JSX.Element {
  const [activeTab, setActiveTab] = useState('overview')

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-kaycoo-teal-dark">System Architecture</h1>
        <p className="text-kaycoo-charcoal-2">
          Complete knowledge graph of the D365 AI Assistant application
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-4 lg:grid-cols-8">
          <TabsTrigger value="overview" className="flex items-center gap-1">
            <Layers className="h-4 w-4" />
            <span className="hidden sm:inline">Overview</span>
          </TabsTrigger>
          <TabsTrigger value="entities" className="flex items-center gap-1">
            <Database className="h-4 w-4" />
            <span className="hidden sm:inline">Entities</span>
          </TabsTrigger>
          <TabsTrigger value="components" className="flex items-center gap-1">
            <Component className="h-4 w-4" />
            <span className="hidden sm:inline">Components</span>
          </TabsTrigger>
          <TabsTrigger value="relationships" className="flex items-center gap-1">
            <GitBranch className="h-4 w-4" />
            <span className="hidden sm:inline">Flow</span>
          </TabsTrigger>
          <TabsTrigger value="attributes" className="flex items-center gap-1">
            <Tag className="h-4 w-4" />
            <span className="hidden sm:inline">Attributes</span>
          </TabsTrigger>
          <TabsTrigger value="ontology" className="flex items-center gap-1">
            <Network className="h-4 w-4" />
            <span className="hidden sm:inline">Ontology</span>
          </TabsTrigger>
          <TabsTrigger value="identifiers" className="flex items-center gap-1">
            <Key className="h-4 w-4" />
            <span className="hidden sm:inline">IDs</span>
          </TabsTrigger>
          <TabsTrigger value="inference" className="flex items-center gap-1">
            <Brain className="h-4 w-4" />
            <span className="hidden sm:inline">AI Logic</span>
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="rounded-lg border border-kaycoo-teal-dark bg-white p-6">
            <h2 className="mb-4 text-2xl font-bold text-kaycoo-teal-dark">Application Overview</h2>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <StatCard title="Database Tables" value="33" icon={Database} />
              <StatCard title="Edge Functions" value="13" icon={Component} />
              <StatCard title="AI Agents" value="4" icon={Brain} />
              <StatCard title="React Components" value="46+" icon={Layers} />
            </div>
          </div>

          <ArchitectureDiagram />
        </TabsContent>

        {/* All Other Tabs */}
        <TabsContent value="entities"><EntitySection /></TabsContent>
        <TabsContent value="components"><ComponentSection /></TabsContent>
        <TabsContent value="relationships"><RelationshipSection /></TabsContent>
        <TabsContent value="attributes"><AttributeSection /></TabsContent>
        <TabsContent value="ontology"><OntologySection /></TabsContent>
        <TabsContent value="identifiers"><IdentifierSection /></TabsContent>
        <TabsContent value="inference"><InferenceSection /></TabsContent>
      </Tabs>
    </div>
  )
}

// Stat Card Component
interface StatCardProps {
  title: string
  value: string
  icon: React.ElementType
}

function StatCard({ title, value, icon: Icon }: StatCardProps) {
  return (
    <div className="rounded-lg border border-kaycoo-teal bg-kaycoo-teal-pale p-4">
      <div className="flex items-center gap-3">
        <Icon className="h-8 w-8 text-kaycoo-teal-dark" />
        <div>
          <div className="text-3xl font-bold text-kaycoo-teal-dark">{value}</div>
          <div className="text-sm text-kaycoo-charcoal-2">{title}</div>
        </div>
      </div>
    </div>
  )
}

// Architecture Diagram Component
function ArchitectureDiagram() {
  return (
    <div className="rounded-lg border border-kaycoo-teal-dark bg-white p-6">
      <h3 className="mb-4 text-xl font-semibold text-kaycoo-teal-dark">System Architecture</h3>
      <div className="space-y-4">
        <LayerBox
          title="React Frontend (Vite)"
          items={['Dashboard', 'Issues', 'Knowledge Base', 'Documents', 'Settings']}
          color="bg-blue-50 border-blue-300"
        />
        <Arrow />
        <LayerBox
          title="Supabase (PostgreSQL + Auth)"
          items={['33 Tables', 'RLS Policies', 'Triggers', 'Functions', 'Auth']}
          color="bg-green-50 border-green-300"
        />
        <Arrow />
        <LayerBox
          title="Kaycoo Agentic Platform"
          items={['issue-recorder', 'kb-search', 'kb-manager', 'innovation-orchestrator']}
          color="bg-purple-50 border-purple-300"
        />
        <Arrow />
        <LayerBox
          title="AI Providers"
          items={['OpenAI GPT-4', 'text-embedding-3-small', 'Moderation']}
          color="bg-orange-50 border-orange-300"
        />
      </div>
    </div>
  )
}

function LayerBox({ title, items, color }: { title: string; items: string[]; color: string }) {
  return (
    <div className={`rounded-lg border-2 p-4 ${color}`}>
      <h4 className="mb-2 font-semibold text-kaycoo-teal-dark">{title}</h4>
      <div className="flex flex-wrap gap-2">
        {items.map((item, i) => (
          <span key={i} className="rounded bg-white px-2 py-1 text-xs font-medium text-kaycoo-charcoal-2">
            {item}
          </span>
        ))}
      </div>
    </div>
  )
}

function Arrow() {
  return (
    <div className="flex justify-center">
      <div className="text-2xl text-kaycoo-teal-dark">↓</div>
    </div>
  )
}
